import { useArchiveDoc, usePatient } from '@hook'
import { ADMIN_MODAL_TYPE, useModalStore } from '@modal'
import type { ArchiveDocumentResource, ArchiveFolderIncluded, ArchiveFolderResource } from '@type/archive.types'
import { getArchiveFacilityName, getArchivePatientName } from '@type/archive.types'
import { DropMenu, SideDrawer, Tab3 } from '@ui'
import type { Option } from '@ui/DropdownBtn/DropMenu'
import { FormDown } from 'grommet-icons'
import moment from 'moment'
import { useMemo, useState, type ReactNode } from 'react'
import DigitizationStatusBadge from '../components/DigitizationStatusBadge'

type Props = {
  onClose: () => void
  folder?: ArchiveFolderResource
  included?: ArchiveFolderIncluded[]
}

const DETAIL_TABS = [
  { value: 'overview', label: 'Overview' },
  { value: 'documents', label: 'Documents' }
] as const

type DetailTab = (typeof DETAIL_TABS)[number]['value']

const CATEGORY_OPTIONS: Option[] = [
  { id: '', label: 'All Categories' },
  { id: 'discharge', label: 'Discharge' },
  { id: 'consultation', label: 'Consultation' },
  { id: 'clinical_notes', label: 'Clinical notes' },
  { id: 'imaging', label: 'Imaging' }
]

const DOC_TYPE_STYLES: Record<string, string> = {
  discharge: 'bg-[#F3E8FF] text-[#7C3AED]',
  consultation: 'bg-[#E0F2FE] text-[#0369A1]',
  clinical_notes: 'bg-[#E8F5E9] text-[#2E7D32]',
  imaging: 'bg-[#E8F5E9] text-[#2E7D32]',
  default: 'bg-[#F3F4F6] text-[#6B7280]'
}

const formatLongDate = (value?: string) => (value ? moment(value).format('MMMM D, YYYY') : '—')

const formatDateRange = (start?: string, end?: string) => {
  if (!start && !end) return '—'
  return `${start ? moment(start).format('MMM YYYY') : '—'} – ${end ? moment(end).format('MMM YYYY') : '—'}`
}

const DetailField = ({ label, value }: { label: string; value: ReactNode }) => (
  <div>
    <p className="text-xs text-[#9CA3AF]">{label}</p>
    <div className="mt-0.5 text-sm font-semibold text-[#111827]">{value || '—'}</div>
  </div>
)

const DetailSection = ({ title, children }: { title: string; children: ReactNode }) => (
  <div className="rounded-xl border border-[#E5E7EB] bg-[#FAFAFA] p-4">
    <h4 className="mb-3 text-xs font-semibold tracking-wide text-[#6B7280]">{title}</h4>
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">{children}</div>
  </div>
)

const DocumentCard = ({ doc }: { doc: ArchiveDocumentResource }) => {
  const typeKey = (doc.attributes.document_type || '').toLowerCase().replace(/\s+/g, '_')
  const badgeClass = DOC_TYPE_STYLES[typeKey] || DOC_TYPE_STYLES.default
  const typeLabel = doc.attributes.document_type || 'Document'

  return (
    <div className="flex items-start gap-3 rounded-xl border border-[#E5E7EB] bg-white p-4 flex-1">
      <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${badgeClass}`}>
        <span className="text-sm font-bold">{typeLabel.charAt(0).toUpperCase()}</span>
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <p className="text-sm font-semibold text-[#111827]">{doc.attributes.title}</p>
          <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${badgeClass}`}>{typeLabel}</span>
        </div>
        <p className="mt-1 text-xs text-[#6B7280]">
          {[
            doc.attributes.document_date ? moment(doc.attributes.document_date).format('D MMM YYYY') : null,
            doc.attributes.clinical_context,
            doc.attributes.specialty
          ]
            .filter(Boolean)
            .join(' · ') || '—'}
        </p>
      </div>
    </div>
  )
}

const FileDetails = ({ onClose, folder, included = [] }: Props) => {
  const [tab, setTab] = useState<DetailTab>('overview')
  const [categoryFilter, setCategoryFilter] = useState('')
  const [categoryLabel, setCategoryLabel] = useState('All Categories')
  const { openModal } = useModalStore()

  const patientId = folder?.relationships.patient?.data?.id
  const { singlePatient } = usePatient({ id: patientId })
  const { documents, isLoading: docsLoading } = useArchiveDoc({
    archive_folder_id: folder?.id,
    enabled: !!folder?.id
  })

  const documentList = useMemo(() => {
    const list = documents?.length ? documents : ([] as ArchiveDocumentResource[])
    if (!categoryFilter) return list
    return list.filter(doc => doc.attributes.document_type?.toLowerCase().replace(/\s+/g, '_') === categoryFilter)
  }, [documents, categoryFilter])

  if (!folder) return null

  const patientName = getArchivePatientName(folder, included)
  const facilityName = getArchiveFacilityName(folder, included)
  const attrs = folder.attributes

  const patientAttrs = singlePatient?.attributes
  const fullName = [patientAttrs?.first_name, patientAttrs?.last_name].filter(Boolean).join(' ') || patientName
  const mrn = patientAttrs?.ref || '—'
  const gender = patientAttrs?.gender ? patientAttrs.gender.charAt(0).toUpperCase() + patientAttrs.gender.slice(1) : '—'

  const subtitle = [attrs.folder_number, facilityName, 'Main Campus'].filter(Boolean).join(' · ')

  const openUpload = () => {
    openModal(ADMIN_MODAL_TYPE.UPLOAD_ARCHIVE_DOCUMENT, { folder, included })
  }

  return (
    <SideDrawer onClose={onClose} title="File Details">
      <div className="px-8 pb-0 pt-1">
        <h3 className="text-xl font-semibold text-[#111827]">{fullName}</h3>
        <p className="mt-1 text-sm text-[#6B7280]">{subtitle}</p>
        <div className="mt-4">
          <Tab3 activeTab={tab} setActiveTab={value => setTab(value as DetailTab)} tabs={[...DETAIL_TABS]} />
        </div>
      </div>

      <div className="overflow-y-auto px-8 py-5 flex-1">
        {tab === 'overview' ? (
          <div className="flex flex-col gap-4">
            <DetailSection title="PATIENT DETAILS">
              <DetailField label="Full Name" value={fullName} />
              <DetailField label="Date of Birth" value={formatLongDate(patientAttrs?.date_of_birth)} />
              <DetailField label="Gender" value={gender} />
              <DetailField label="MRN" value={mrn} />
              <DetailField label="Old Folder no." value={attrs.folder_number} />
            </DetailSection>

            <DetailSection title="CLINICAL OWNERSHIP">
              <DetailField label="Facility" value={facilityName} />
              <DetailField label="Date Range" value={formatDateRange(attrs.date_range_start, attrs.date_range_end)} />
            </DetailSection>

            <DetailSection title="FILE LOCATION">
              <DetailField label="Room" value={attrs.physical_location_name} />
              <DetailField label="Shelf" value={attrs.shelf} />
              <DetailField label="Cabinet" value={attrs.column} />
              <DetailField label="Box" value={attrs.section} />
            </DetailSection>

            <DetailSection title="Status">
              <DetailField
                label="Digitization Status"
                value={<DigitizationStatusBadge status={attrs.digitization_status} />}
              />
            </DetailSection>
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            <div className="flex">
              <DropMenu
                handleClick={option => {
                  setCategoryFilter(option.id)
                  setCategoryLabel(option.label)
                }}
                icon={<FormDown size="small" />}
                options={CATEGORY_OPTIONS}
                title={categoryLabel}
                titleClassName="!rounded-full !border !border-[#D1D5DB] !bg-white !px-4 !py-2 !text-sm !text-[#374151]"
              />
            </div>
            {docsLoading ? (
              <p className="text-sm text-[#6B7280]">Loading documents…</p>
            ) : documentList.length === 0 ? (
              <p className="text-sm text-[#6B7280]">No scanned documents yet.</p>
            ) : (
              documentList.map(doc => <DocumentCard doc={doc} key={doc.id} />)
            )}
          </div>
        )}
      </div>

      <div className="border-t border-[#E5E7EB] px-8 py-4 mt-auto">
        <button
          className="flex w-full items-center justify-center gap-2 rounded-full bg-[#F3F4F6] px-4 py-3 text-sm font-medium text-[#374151] hover:bg-[#E5E7EB]"
          onClick={openUpload}
          type="button"
        >
          <svg fill="none" height="16" viewBox="0 0 16 16" width="16" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M8 11.333V2.666M8 2.666L4.667 6M8 2.666L11.333 6M2.667 13.333h10.666"
              stroke="currentColor"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="1.5"
            />
          </svg>
          Upload scanned document
        </button>
      </div>
    </SideDrawer>
  )
}

export default FileDetails
